import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Card,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import {
    ContactSupportRounded,
    SearchRounded,
    SupportAgentRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ErrorContext } from "../../components/GympinPagesProvider";
import { Support_query } from "../../network/api/support.api";
import _SupportCardItem from "./_SupportCardItem";


const _SupportList = ({ onOpenNewTicket }) => {
    const theme = useTheme();
    const error = useContext(ErrorContext);
    const navigate = useNavigate();
    const corporate = useSelector(({ corporate }) => corporate.corporate);

    const [supportData, setSupportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');

    useEffect(() => {
        document.title = 'تیکت‌های پشتیبانی | جیم‌پین سازمانی';
        loadTickets();
    }, [corporate?.Id]);

    const loadTickets = () => {
        if (!corporate?.Id) return;
        setLoading(true);
        Support_query({
            queryType: "FILTER",
            CorporateId: corporate.Id,
            paging: { Page: 0, Size: 50, orderBy: "Id", Desc: true },
        })
            .then(result => {
                setSupportData(result.data.Data);
            })
            .catch(e => {
                const msg = e?.response?.data?.Message || "خطا در دریافت لیست تیکت‌ها";
                error.showError({ message: msg });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const filteredTickets = useMemo(() => {
        const list = supportData?.content || [];
        return list.filter(item => {
            const matchesSearch = !searchTerm.trim() ||
                (item.Title && item.Title.toLowerCase().includes(searchTerm.toLowerCase().trim())) ||
                (item.Id && String(item.Id).includes(searchTerm.trim()));

            let matchesStatus = true;
            if (statusFilter === 'OPEN') {
                matchesStatus = item.Status !== 'COMPLETE' && item.Status !== 'CANCEL';
            } else if (statusFilter === 'CLOSED') {
                matchesStatus = item.Status === 'COMPLETE' || item.Status === 'CANCEL';
            }

            return matchesSearch && matchesStatus;
        });
    }, [supportData, searchTerm, statusFilter]);

    const formatPersianDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('fa-IR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Search & Tabs Toolbar */}
            <Card
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 3.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" justifyContent="space-between">
                    <Tabs
                        value={statusFilter}
                        onChange={(e, val) => setStatusFilter(val)}
                        textColor="primary"
                        indicatorColor="primary"
                        sx={{
                            minHeight: 40,
                            '& .MuiTab-root': {
                                minHeight: 40,
                                py: 0.5,
                                px: 1.5,
                                fontSize: '0.85rem',
                                fontWeight: 700,
                            },
                        }}
                    >
                        <Tab label={`همه (${supportData?.content?.length || 0})`} value="ALL" />
                        <Tab label="در جریان" value="OPEN" />
                        <Tab label="پایان‌یافته" value="CLOSED" />
                    </Tabs>

                    <TextField
                        size="small"
                        placeholder="جستجو در موضوع یا شماره تیکت..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRounded sx={{ fontSize: 20, color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: { xs: '100%', sm: 260 },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2.5,
                            },
                        }}
                    />
                </Stack>
            </Card>

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={36} thickness={4} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        در حال بارگذاری تیکت‌ها...
                    </Typography>
                </Box>
            )}

            {/* Empty State */}
            {!loading && filteredTickets.length === 0 && (
                <Card
                    elevation={0}
                    sx={{
                        p: { xs: 4, md: 6 },
                        borderRadius: 4,
                        textAlign: 'center',
                        border: '1px dashed',
                        borderColor: 'divider',
                        bgcolor: 'action.hover',
                    }}
                >
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.50',
                            color: 'primary.main',
                            mb: 2,
                        }}
                    >
                        <SupportAgentRounded sx={{ fontSize: 38 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                        {searchTerm ? "تیکتی با این مشخصات یافت نشد" : "درخواست پشتیبانی ثبت نشده است"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto', mb: 3 }}>
                        {searchTerm
                            ? "عبارت دیگری را جستجو کنید."
                            : "در صورت نیاز می‌توانید تیکت جدید ارسال نمایید."}
                    </Typography>
                </Card>
            )}

            {/* Ticket Cards List */}
            {!loading && filteredTickets.length > 0 && (
                <Stack spacing={1.5}>
                    {filteredTickets.map(item => (
                        <_SupportCardItem
                            key={item.Id}
                            item={item}
                            onClick={() => navigate(`/support/detail/${item.Id}`)}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export default _SupportList;

